import { useEffect } from "react"
import { useLocation } from "react-router-dom"

const SUFFIX = "Qualneo"

const ROUTE_TITLES: Record<string, string> = {
    "/dashboard": "Tableau de bord",
    "/tasks": "Tâches",

    // CRM
    "/crm/accounts": "Comptes",
    "/crm/contacts": "Contacts",
    "/crm/opportunities": "Opportunités",
    "/crm/needs-assessments": "Analyses de besoins",

    // Training
    "/training/programs": "Programmes",
    "/training/sessions": "Sessions",
    "/training/calendar": "Calendrier",
    "/training/registrations": "Inscriptions",
    "/training/enrollment-pipeline": "Pipeline d'inscription",
    "/training/attendance": "Émargement",
    "/training/juries": "Jurys",
    "/training/competency-blocks": "Blocs de compétences",
    "/training/vae": "Parcours VAE",

    // Commercial
    "/commercial/dashboard": "Tableau de bord commercial",
    "/commercial/quotes": "Devis",
    "/commercial/quotes/new": "Nouveau devis",
    "/commercial/invoices": "Factures",
    "/commercial/contracts": "Contrats",
    "/commercial/opco": "Annuaire OPCO",
    "/commercial/opco-files": "Dossiers OPCO",

    // Qualiopi
    "/qualiopi/indicators": "Indicateurs Qualiopi",
    "/qualiopi/evaluations": "Évaluations",
    "/qualiopi/complaints": "Réclamations",
    "/qualiopi/improvements": "Améliorations",
    "/qualiopi/venues": "Lieux de formation",
    "/qualiopi/watch": "Veille",
    "/qualiopi/surveys": "Enquêtes",
    "/qualiopi/marketing-kpis": "KPIs Marketing",
    "/qualiopi/surveys/assignments": "Invitations aux enquêtes",
    "/qualiopi/simulator": "Simulateur d'audit",

    // Documents
    "/documents": "Documents",
    "/documents/generate": "Génération de documents",

    // HR
    "/hr/staff": "Personnel",
    "/hr/instructors": "Formateurs",
    "/hr/self-verification": "Auto-vérification",
    "/hr/self-verification/:id": "Détail auto-vérification",
    "/hr/staff-evaluations": "Évaluations personnel",
    "/hr/staff-evaluations/:id": "Détail évaluation",
    "/hr/competency-evaluations": "Évaluations des compétences",
    "/hr/professional-interviews": "Entretiens professionnels",
    "/hr/vendors": "Prestataires",
    "/hr/vendor-contracts": "Contrats prestataires",

    // Messaging
    "/messages": "Messagerie",

    // Admin
    "/admin/email-templates": "Modèles d'emails",
    "/admin/document-templates": "Modèles de documents",
    "/admin/scheduled-jobs": "Tâches planifiées",
    "/admin/exports": "Exports",
    "/admin/audit-log": "Journal d'audit",
    "/admin/marketing-kpis": "KPIs Marketing",
    "/admin/competency-frameworks": "Référentiels de compétences",
    "/admin/cpf-validation": "Validation CPF",
    "/admin/public-catalog": "Catalogue public",
    "/admin/disability-organizations": "Organismes handicap",
    "/admin/email-logs": "Journal des emails",

    // Settings
    "/settings": "Paramètres",

    // Reports
    "/reports/bpf": "Bilan pédagogique et financier",
    "/reports/statistics": "Statistiques",

    // Auth
    "/login": "Connexion",
    "/register": "Inscription",
    "/forgot-password": "Mot de passe oublié",
    "/reset-password": "Réinitialiser le mot de passe",

    // Portal
    "/portal/login": "Portail - Connexion",
    "/portal/dashboard": "Portail - Tableau de bord",
    "/portal/sessions": "Portail - Sessions",
    "/portal/attendance": "Portail - Émargement",
    "/portal/evaluations": "Portail - Évaluations",
    "/portal/documents": "Portail - Documents",
    "/portal/profile": "Portail - Mon profil",
}

/**
 * Sous-pages d'une entité : leur propre titre.
 * Lues avant le titre de la liste parente.
 * Sinon la checklist d'une session s'intitulait « Sessions ».
 */
const SUB_ROUTE_TITLES: readonly (readonly [RegExp, string])[] = [
    // /training/sessions/<id>/checklist
    [/^\/training\/sessions\/[^/]+\/checklist$/, "Checklist des livrables"],
    // /training/sessions/<id>/dashboard
    [/^\/training\/sessions\/[^/]+\/dashboard$/, "Tableau de bord session"],
    // /training/programs/<id>/competencies
    [/^\/training\/programs\/[^/]+\/competencies$/, "Matrice de compétences"],
    // /training/registrations/<id>/audit-folder
    [/^\/training\/registrations\/[^/]+\/audit-folder$/, "Dossier de preuves"],
]

/**
 * Retire le préfixe /:orgSlug du pathname pour retrouver le chemin logique.
 * Les routes publiques (/login, /portal/*, /jury/*) ne sont pas préfixées.
 */
function stripOrgSlug(pathname: string): string {
    const publicPrefixes = ["/login", "/register", "/forgot-password", "/reset-password", "/accept-invite", "/portal", "/jury"]
    if (publicPrefixes.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p + "?"))) {
        return pathname
    }
    // Pour les routes authentifiées : /:orgSlug/dashboard -> /dashboard
    const segments = pathname.split("/").filter(Boolean)
    if (segments.length >= 2) {
        return "/" + segments.slice(1).join("/")
    }
    // Fallback: si un seul segment, c'est l'orgSlug sans sous-route
    return "/"
}

function findTitle(pathname: string): string {
    const normalizedPath = stripOrgSlug(pathname)

    // Exact match first
    const exact = ROUTE_TITLES[normalizedPath]
    if (exact) return exact

    const subRoute = SUB_ROUTE_TITLES.find(([pattern]) => pattern.test(normalizedPath))
    if (subRoute) return subRoute[1]

    // For dynamic routes like /crm/accounts/:id or /training/programs/:id,
    // match the parent path pattern
    const segments = normalizedPath.split("/").filter(Boolean)
    if (segments.length >= 2) {
        // Try parent path (e.g., /crm/accounts for /crm/accounts/123)
        const parentPath = "/" + segments.slice(0, 2).join("/")
        const parentTitle = ROUTE_TITLES[parentPath]
        if (parentTitle) return parentTitle
    }

    // Needs assessment detail
    if (normalizedPath.match(/^\/crm\/needs-assessments\/[^/]+$/)) return "Détail analyse des besoins"

    // Indicator detail
    if (normalizedPath.match(/^\/qualiopi\/indicators\/[^/]+$/)) return "Détail indicateur"

    // Evaluation detail
    if (normalizedPath.match(/^\/qualiopi\/evaluations\/[^/]+$/)) return "Détail évaluation"

    // Enrollment detail
    if (normalizedPath.match(/^\/training\/enrollments\/[^/]+$/)) return "Détail parcours d'inscription"

    // Registration detail
    if (normalizedPath.match(/^\/training\/registrations\/[^/]+$/)) return "Détail inscription"

    // Portal attendance sign
    if (normalizedPath.match(/^\/portal\/attendance\/[^/]+\/sign$/)) return "Portail - Signer ma présence"

    // Portal session detail
    if (normalizedPath.startsWith("/portal/sessions/")) return "Portail - Détail session"

    // Jury portal
    if (normalizedPath.startsWith("/jury/")) return "Portail Jury"

    // Settings sub-routes
    if (normalizedPath.startsWith("/settings")) return "Paramètres"

    return ""
}

export function RouteTitle() {
    const location = useLocation()

    useEffect(() => {
        const title = findTitle(location.pathname)
        document.title = title ? `${title} - ${SUFFIX}` : SUFFIX
    }, [location.pathname])

    return null
}
